/**
 * Image-editor-local convenience wrapper around the imgly background-removal
 * client-side entity.
 *
 * The shared `invokeClientEntity` requires `{ userId, archeId }` context for
 * per-(user, arche) rate-limit scoping. Threading that through every call
 * site inside the editor would be noise — this hook captures the values
 * once via `useAuth()` and the editor's `ARCHE_ID` constant, then returns
 * a single-arg callable that internal call sites can use directly.
 *
 * NOT a "shared client capability library" (per CLAUDE.md's Entity-config-
 * driven dispatch rule): the dispatch still goes through the entity
 * registry and is gated by the same entity-config invariants the plugin
 * path observes. This is local sugar around a properly-modeled entity.
 */

import { useCallback } from 'react';
import { invokeClientEntity } from '@/lib/client-entity-registry';
import {
  IMGLY_BACKGROUND_REMOVAL_ENTITY_ID,
  type ImglyBackgroundRemovalInput,
  type ImglyBackgroundRemovalResult,
} from '@/lib/client-entity-executors';
import { useAuth } from '@/auth';
import { ARCHE_ID } from '../types/image-editor.types';

export function useImglyRemoveBackground(): (image: Blob) => Promise<Blob> {
  const { user } = useAuth();
  return useCallback(
    async (image: Blob): Promise<Blob> => {
      if (!user?.userId) {
        throw new Error('background removal requires an authenticated user');
      }
      const result = await invokeClientEntity<
        ImglyBackgroundRemovalInput,
        ImglyBackgroundRemovalResult
      >(IMGLY_BACKGROUND_REMOVAL_ENTITY_ID, { image }, { userId: user.userId, archeId: ARCHE_ID });
      return result.image;
    },
    [user?.userId],
  );
}
